import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import CONFIG from '../../config.js';
import { getImgUrl } from '../../funciones.js';
import './css/ImprimirBoletin.css';

function ImprimirBoletin({ acceder, configuracion }) {
    const { studentId, cohorteId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const instanciaSeleccionadaId = searchParams.get('instancia');

    const [perfil, setPerfil] = useState(null);
    const [cursos, setCursos] = useState([]);
    const [valoraciones, setValoraciones] = useState([]);
    const [instancias, setInstancias] = useState([]);
    const [trayectorias, setTrayectorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        // Redirigir si no tiene acceso
        if (!acceder) {
            localStorage.clear();
            navigate('/');
            return;
        }

        const fetchDatos = async () => {
            setLoading(true);
            try {
                // 1. Obtener detalles del estudiante
                const resUser = await axios.post(`${CONFIG.API_URL}/listarUsuarios.php`, {
                    id_usuario: studentId,
                    modo: 'buscarFichalUsuario'
                });
                if (!resUser.data.error) {
                    setPerfil(resUser.data.info);
                } else {
                    setError(true);
                }

                // 2. Obtener cursos, valoraciones e instancias para esta cohorte
                const resGrades = await axios.post(`${CONFIG.API_URL}/operarCalificaciones.php`, {
                    modo: 'buscarCursosyCalificaciones',
                    id_usuario: studentId,
                    id_cohorte: cohorteId,
                    ciclo: new Date().getFullYear()
                });
                if (resGrades.data.resultado) {
                    setCursos(resGrades.data.cursos || []);
                    setValoraciones(resGrades.data.valoraciones || []);
                    setInstancias(resGrades.data.instancias || []);
                    setTrayectorias(resGrades.data.trayectorias || []);
                } else {
                    setError(true);
                }
            } catch (err) {
                console.error("Error al cargar datos del boletín:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        if (studentId && cohorteId) {
            fetchDatos();
        }
    }, [studentId, cohorteId, acceder, navigate]);

    if (loading) {
        return (
            <div className="boletin-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '15px' }}>
                <div className="spinner-border text-primary" role="status"></div>
                <span className="text-secondary fw-semibold">Generando reporte de calificaciones...</span>
            </div>
        );
    }

    if (error || !perfil) {
        return (
            <div className="boletin-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '15px' }}>
                <i className="fa-solid fa-triangle-exclamation text-danger" style={{ fontSize: '3rem' }}></i>
                <h4 className="text-dark">Error al cargar el boletín</h4>
                <p className="text-muted text-center" style={{ maxWidth: '400px' }}>No se pudo obtener la información de calificaciones para este alumno. Verifique que existan datos cargados para la cohorte seleccionada.</p>
                <button className="boletin-btn boletin-btn-secondary" onClick={() => window.close()}>Cerrar Ventana</button>
            </div>
        );
    }

    // Extraer metadatos de los cursos
    const primerCurso = cursos[0] || {};
    const cohorteNombre = primerCurso.nombre_formacion || 'General';
    const cohorteAño = primerCurso.año || '';
    const divisionDenom = primerCurso.denominacion || '';
    const ordenAño = primerCurso.orden || '';

    const selectedInst = instancias.find(i => String(i.id) === String(instanciaSeleccionadaId));
    const esFinal = !instanciaSeleccionadaId || (selectedInst?.nombre_instancia?.toLowerCase() === 'final');

    const tieneTrayectoriaInstancia = !esFinal && selectedInst && trayectorias.some(t => String(t.id_instancia) === String(selectedInst.id));

    const obtenerTextoObservacion = (c, inst, val) => {
        if (!val || !(val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada')) {
            return '';
        }

        const valorCal = val.valor || '';
        const obsDocente = val.observacion || '';

        const trayEncontrada = trayectorias.find(
            t => String(t.id_instancia) === String(inst.id) && (
                String(t.id_curso_grupo) === String(c.id_curso_grupo) ||
                String(t.id_curso_grupo) === String(c.id_grupo)
            )
        );

        if (trayEncontrada) {
            const crit = trayEncontrada.criterios?.find(
                cr => String(cr.criterio).trim().toLowerCase() === String(valorCal).trim().toLowerCase()
            );
            const descCriterio = crit?.descripcion || '';

            if (descCriterio && obsDocente && obsDocente.trim() !== '') {
                return `${descCriterio} — Comentario: "${obsDocente}"`;
            } else if (descCriterio) {
                return descCriterio;
            } else if (obsDocente && obsDocente.trim() !== '') {
                return obsDocente;
            }
            return '';
        }

        return obsDocente;
    };

    // Agrupar observaciones de docentes para mostrarlas en un apartado ordenado (solo para boletín final)
    const observacionesList = [];
    if (esFinal) {
        cursos.forEach(c => {
            instancias.forEach(inst => {
                const val = valoraciones.find(v => String(v.id_instancia) === String(inst.id) && String(v.id_curso) === String(c.id));
                const textoObs = obtenerTextoObservacion(c, inst, val);
                if (textoObs && textoObs.trim() !== '') {
                    observacionesList.push({
                        materia: c.nombre_espacio,
                        instancia: inst.nombre_instancia,
                        texto: textoObs
                    });
                }
            });
        });
    }

    // ── Descargar PDF Directo usando html2pdf ─────────────────────────────
    const descargarPDFDirecto = () => {
        const element = document.querySelector('.boletin-container');
        if (!element) return;

        const nombreArchivo = `Boletin_${perfil?.apellido || 'Estudiante'}_${perfil?.nombre || ''}.pdf`;

        const opt = {
            margin: [8, 8, 8, 8],
            filename: nombreArchivo,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
        };

        if (window.html2pdf) {
            window.html2pdf().set(opt).from(element).save();
        } else {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
            script.onload = () => {
                window.html2pdf().set(opt).from(element).save();
            };
            document.body.appendChild(script);
        }
    };

    // ── Helper para cargar ExcelJS dinámicamente ─────────────────────────
    const cargarExcelJS = () => {
        return new Promise((resolve, reject) => {
            if (window.ExcelJS) return resolve(window.ExcelJS);
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.3.0/exceljs.min.js';
            script.onload = () => resolve(window.ExcelJS);
            script.onerror = reject;
            document.body.appendChild(script);
        });
    };

    // ── Helper para convertir URL de imagen en Base64 con dimensiones original ────────
    const obtenerBase64Imagen = (url) => {
        return new Promise((resolve) => {
            if (!url) return resolve(null);
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/png');
                    resolve({
                        base64: dataURL,
                        width: img.width,
                        height: img.height
                    });
                } catch {
                    resolve(null);
                }
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });
    };

    // ── Descargar Excel del Boletín con Logo Institucional Proporcional ────────
    const descargarExcelBoletin = async () => {
        try {
            const ExcelJS = await cargarExcelJS();
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Boletin");

            // Cargar Logo Institucional
            const logoUrl = configuracion?.logo_solo ? getImgUrl(configuracion.logo_solo) : null;
            const logoObj = logoUrl ? await obtenerBase64Imagen(logoUrl) : null;

            let startRow = 1;

            if (logoObj && logoObj.base64) {
                const imageId = workbook.addImage({
                    base64: logoObj.base64,
                    extension: 'png',
                });

                const origW = logoObj.width || 120;
                const origH = logoObj.height || 60;
                const maxHeight = 55;
                const maxWidth = 150;
                const ratio = Math.min(maxWidth / origW, maxHeight / origH);
                const finalWidth = Math.round(origW * ratio);
                const finalHeight = Math.round(origH * ratio);

                worksheet.addImage(imageId, {
                    tl: { col: 0, row: 0 },
                    ext: { width: finalWidth, height: finalHeight }
                });
                startRow = 4;
            }

            worksheet.getCell(`B${startRow}`).value = "INFORME DEL ESTADO DE AVANCE DE LOS APRENDIZAJES";
            worksheet.getCell(`B${startRow}`).font = { bold: true, size: 14, color: { argb: '0F172A' } };

            worksheet.addRow([]);
            worksheet.addRow(["Estudiante:", `${perfil?.apellido || ''}, ${perfil?.nombre || ''}`]);
            worksheet.addRow(["Documento:", perfil?.documento || '—']);
            worksheet.addRow(["Cohorte / Formación:", cohorteNombre]);
            worksheet.addRow(["Curso / División:", `${ordenAño ? ordenAño + '° año' : ''} - Div. ${divisionDenom || ''}`]);
            worksheet.addRow([]);

            let headers = [];
            if (esFinal) {
                headers = ["Espacio Curricular", ...instancias.map(i => i.nombre_instancia)];
            } else if (tieneTrayectoriaInstancia) {
                headers = ["Espacio Curricular", "Descripción de Trayectoria", `${selectedInst?.nombre_instancia || ''} (Valoración)`, "Indicador de Logro y Comentario"];
            } else {
                headers = ["Espacio Curricular", selectedInst?.nombre_instancia || 'Valoración', "Observaciones"];
            }

            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '334155' }
                };
            });

            if (esFinal) {
                cursos.forEach(c => {
                    const row = [c.nombre_espacio];
                    instancias.forEach(inst => {
                        const val = valoraciones.find(v => String(v.id_instancia) === String(inst.id) && String(v.id_curso) === String(c.id));
                        const esAprob = val && (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada');
                        row.push(esAprob && val?.valor ? val.valor : '—');
                    });
                    worksheet.addRow(row);
                });
            } else if (tieneTrayectoriaInstancia) {
                cursos.forEach(c => {
                    const val = selectedInst ? valoraciones.find(
                        (v) => String(v.id_instancia) === String(selectedInst.id) && String(v.id_curso) === String(c.id)
                    ) : null;
                    const esAprob = val && (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada');
                    const valorCal = esAprob && val?.valor ? String(val.valor).trim() : '—';

                    const trayEncontrada = selectedInst ? trayectorias.find(
                        t => String(t.id_instancia) === String(selectedInst.id) && (
                            String(t.id_curso_grupo) === String(c.id_curso_grupo) ||
                            String(t.id_curso_grupo) === String(c.id_grupo)
                        )
                    ) : null;

                    const descTray = trayEncontrada?.descripcion || trayEncontrada?.titulo || '—';
                    let indicadorComent = '—';
                    if (esAprob && trayEncontrada) {
                        const crit = trayEncontrada.criterios?.find(
                            cr => String(cr.criterio).trim().toLowerCase() === valorCal.toLowerCase()
                        );
                        const descCriterio = crit?.descripcion || '';
                        let coment = val?.observacion || '';
                        if (descCriterio && coment) {
                            indicadorComent = `${descCriterio} — Comentario: "${coment}"`;
                        } else if (descCriterio) {
                            indicadorComent = descCriterio;
                        } else if (coment) {
                            indicadorComent = coment;
                        }
                    } else if (esAprob && val?.observacion) {
                        indicadorComent = val.observacion;
                    }

                    worksheet.addRow([c.nombre_espacio, descTray, valorCal, indicadorComent]);
                });
            } else {
                cursos.forEach(c => {
                    const val = selectedInst ? valoraciones.find(
                        (v) => String(v.id_instancia) === String(selectedInst.id) && String(v.id_curso) === String(c.id)
                    ) : null;
                    const esAprob = val && (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada');
                    worksheet.addRow([
                        c.nombre_espacio,
                        esAprob && val?.valor ? val.valor : '—',
                        esAprob && val?.observacion ? val.observacion : '—'
                    ]);
                });
            }

            worksheet.columns.forEach(column => {
                column.width = 28;
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Boletin_${perfil?.apellido || 'Estudiante'}_${perfil?.nombre || ''}.xlsx`;
            link.click();
        } catch (err) {
            console.error("Error al exportar Excel con ExcelJS:", err);
            // Fallback
            const data = [];
            data.push(["INFORME DEL ESTADO DE AVANCE DE LOS APRENDIZAJES"]);
            data.push(["Estudiante:", `${perfil?.apellido || ''}, ${perfil?.nombre || ''}`]);
            data.push(["Documento:", perfil?.documento || '—']);
            data.push(["Cohorte / Formación:", cohorteNombre]);
            data.push(["Curso / División:", `${ordenAño ? ordenAño + '° año' : ''} - Div. ${divisionDenom || ''}`]);
            data.push([]);
            const ws = XLSX.utils.aoa_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Boletin");
            XLSX.writeFile(wb, `Boletin_${perfil?.apellido || 'Estudiante'}_${perfil?.nombre || ''}.xlsx`);
        }
    };

    return (
        <div className="boletin-body">
            {/* Barra de Acciones (Oculta en Impresión) */}
            <div className="boletin-acciones-bar no-print ms-5 ps-5 mt-5 pt-5">
                <div className="boletin-acciones-titulo">
                    <i className="fa-solid fa-file-invoice"></i>
                    Vista Previa del Boletín Digital
                </div>
                <div className="boletin-acciones-botones">
                    <button className="boletin-btn boletin-btn-primary" onClick={descargarPDFDirecto} title="Descargar el boletín en formato PDF">
                        <i className="fa-solid fa-file-pdf me-1"></i>
                        Descargar PDF
                    </button>
                    <button className="boletin-btn btn btn-success" onClick={descargarExcelBoletin} title="Descargar el boletín en formato Excel (.xlsx)">
                        <i className="fa-solid fa-file-excel me-1"></i>
                        Descargar Excel
                    </button>
                    <button className="boletin-btn boletin-btn-secondary" onClick={() => window.close()} title="Cerrar ventana">
                        <i className="fa-solid fa-xmark me-1"></i>
                        Cerrar
                    </button>
                </div>
            </div>

            {/* Contenedor del Boletín */}
            <div className="boletin-container">
                {/* Encabezado */}
                <div className="boletin-header">
                    <div className="boletin-logo-sec">
                        {configuracion?.logo_solo ? (
                            <img 
                                src={getImgUrl(configuracion.logo_solo)} 
                                alt="Logo" 
                                className="boletin-logo-img"
                            />
                        ) : (
                            <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>
                                TE
                            </div>
                        )}
                        <div className="boletin-school-info">
                            <h1>INFORME DEL ESTADO DE AVANCE DE LOS APRENDIZAJES</h1>
                            <p>Registro de Calificaciones y Valoración Pedagógica</p>
                        </div>
                    </div>
                    <div className="boletin-titulo-doc">
                        <p>Ciclo Lectivo: {cohorteAño || new Date().getFullYear()}</p>
                    </div>
                </div>

                {/* Datos del Estudiante */}
                <div className="boletin-alumno-card">
                    <div className="boletin-alumno-grid">
                        <div className="boletin-info-item">
                            <span className="boletin-info-label">Estudiante</span>
                            <span className="boletin-info-value">{perfil.apellido}, {perfil.nombre}</span>
                        </div>
                        <div className="boletin-info-item">
                            <span className="boletin-info-label">Documento</span>
                            <span className="boletin-info-value">{perfil.documento || '—'}</span>
                        </div>
                        <div className="boletin-info-item">
                            <span className="boletin-info-label">Cohorte / Formación</span>
                            <span className="boletin-info-value">{cohorteNombre}</span>
                        </div>
                        <div className="boletin-info-item">
                            <span className="boletin-info-label">Curso / División</span>
                            <span className="boletin-info-value">
                                {ordenAño ? `${ordenAño}° año` : '—'}
                                {divisionDenom ? ` - Div. ${divisionDenom}` : ''}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Tabla de Calificaciones */}
                <div className="boletin-table-wrapper">
                    <table className="boletin-table">
                        <thead>
                            {esFinal ? (
                                <tr>
                                    <th style={{ width: '25%' }}>Espacio Curricular</th>
                                    {instancias.map((inst) => (
                                        <th key={inst.id} style={{ textAlign: 'center' }}>
                                            {inst.nombre_instancia}
                                        </th>
                                    ))}
                                </tr>
                            ) : tieneTrayectoriaInstancia ? (
                                <tr>
                                    <th style={{ width: '18%' }}>Espacio Curricular</th>
                                    <th style={{ width: '37%' }}>Descripción de Trayectoria</th>
                                    <th style={{ textAlign: 'center', width: '12%' }}>
                                        {selectedInst?.nombre_instancia} (Valoración)
                                    </th>
                                    <th style={{ width: '33%' }}>Indicador de Logro y Comentario</th>
                                </tr>
                            ) : (
                                <tr>
                                    <th style={{ width: '22%' }}>Espacio Curricular</th>
                                    <th style={{ textAlign: 'center', width: '14%' }}>
                                        {selectedInst?.nombre_instancia}
                                    </th>
                                    <th style={{ width: '64%' }}>Observaciones</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {esFinal ? (
                                cursos.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <div className="boletin-materia-nombre">
                                                {c.nombre_espacio}
                                            </div>
                                        </td>
                                        {instancias.map((inst) => {
                                            const val = valoraciones.find(
                                                (v) => String(v.id_instancia) === String(inst.id) && String(v.id_curso) === String(c.id)
                                            );
                                            const tieneCal = val && val.valor !== null && val.valor !== '' && 
                                                             (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada');
                                            const valorNum = tieneCal ? parseFloat(val.valor) : null;

                                            return (
                                                <td key={inst.id} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {tieneCal ? (
                                                        <span className={`boletin-grade-badge ${valorNum !== null && valorNum < 6 ? 'text-danger' : ''}`}>
                                                            {val.valor}
                                                        </span>
                                                    ) : (
                                                        <span className="boletin-grade-empty"></span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            ) : tieneTrayectoriaInstancia ? (
                                cursos.map((c) => {
                                    const val = selectedInst ? valoraciones.find(
                                        (v) => String(v.id_instancia) === String(selectedInst.id) && String(v.id_curso) === String(c.id)
                                    ) : null;
                                    const esAprobadaOPublicada = val && (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada');
                                    const tieneCal = val && val.valor !== null && val.valor !== '' && esAprobadaOPublicada;
                                    const valorNum = tieneCal ? parseFloat(val.valor) : null;
                                    const valorCal = tieneCal ? String(val.valor).trim() : '';

                                    const trayEncontrada = selectedInst ? trayectorias.find(
                                        t => String(t.id_instancia) === String(selectedInst.id) && (
                                            String(t.id_curso_grupo) === String(c.id_curso_grupo) ||
                                            String(t.id_curso_grupo) === String(c.id_grupo)
                                        )
                                    ) : null;

                                    const descTrayectoria = trayEncontrada?.descripcion || trayEncontrada?.titulo || '—';

                                    let indicadorComentario = '—';
                                    if (tieneCal && trayEncontrada) {
                                        const crit = trayEncontrada.criterios?.find(
                                            cr => String(cr.criterio).trim().toLowerCase() === valorCal.toLowerCase()
                                        );
                                        const descCriterio = crit?.descripcion || '';
                                        
                                        let comentarioEst = '';
                                        if (trayEncontrada.evaluaciones && Array.isArray(trayEncontrada.evaluaciones)) {
                                            const evalEst = trayEncontrada.evaluaciones.find(
                                                e => e.comentario && e.comentario.trim() !== ''
                                            );
                                            if (evalEst) comentarioEst = evalEst.comentario;
                                        }
                                        if (!comentarioEst && val?.observacion) {
                                            comentarioEst = val.observacion;
                                        }

                                        if (descCriterio && comentarioEst) {
                                            indicadorComentario = `${descCriterio} — Comentario: "${comentarioEst}"`;
                                        } else if (descCriterio) {
                                            indicadorComentario = descCriterio;
                                        } else if (comentarioEst) {
                                            indicadorComentario = comentarioEst;
                                        }
                                    }

                                    return (
                                        <tr key={c.id}>
                                            <td>
                                                <div className="boletin-materia-nombre">
                                                    {c.nombre_espacio}
                                                </div>
                                            </td>
                                            <td style={{ fontSize: '0.85rem', verticalAlign: 'middle', padding: '8px' }}>
                                                {descTrayectoria}
                                            </td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                {tieneCal ? (
                                                    <span className={`boletin-grade-badge ${valorNum !== null && valorNum < 6 ? 'text-danger' : ''}`}>
                                                        {val.valor}
                                                    </span>
                                                ) : (
                                                    <span className="boletin-grade-empty"></span>
                                                )}
                                            </td>
                                            <td style={{ fontSize: '0.85rem', verticalAlign: 'middle', padding: '8px' }}>
                                                {indicadorComentario}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                cursos.map((c) => {
                                    const val = selectedInst ? valoraciones.find(
                                        (v) => String(v.id_instancia) === String(selectedInst.id) && String(v.id_curso) === String(c.id)
                                    ) : null;
                                    const esAprobadaOPublicada = val && (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada');
                                    const tieneCal = val && val.valor !== null && val.valor !== '' && esAprobadaOPublicada;
                                    const valorNum = tieneCal ? parseFloat(val.valor) : null;
                                    const observacion = esAprobadaOPublicada ? (val?.observacion || '—') : '—';

                                    return (
                                        <tr key={c.id}>
                                            <td>
                                                <div className="boletin-materia-nombre">
                                                    {c.nombre_espacio}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                {tieneCal ? (
                                                    <span className={`boletin-grade-badge ${valorNum !== null && valorNum < 6 ? 'text-danger' : ''}`}>
                                                        {val.valor}
                                                    </span>
                                                ) : (
                                                    <span className="boletin-grade-empty"></span>
                                                )}
                                            </td>
                                            <td style={{ fontSize: '0.85rem', verticalAlign: 'middle', padding: '8px' }}>
                                                {observacion}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Observaciones de los Docentes (Solo si hay) */}
                {observacionesList.length > 0 && (
                    <div className="boletin-observaciones-sec">
                        <div className="boletin-observaciones-titulo">
                            <i className="fa-regular fa-comment-dots"></i>
                            Observaciones y Aclaraciones Pedagógicas
                        </div>
                        {observacionesList.map((obs, idx) => (
                            <div key={idx} className="boletin-observacion-row">
                                <strong>{obs.materia} ({obs.instancia}):</strong> {obs.texto}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ImprimirBoletin;
